import { useState, useMemo, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../../context/useAuth';
import { useOrg } from '../../../context/OrgContext';
import { getInstructorAnalyticsApi } from '../../../api/apiServices';
import InstructorReviewsManager from './components/InstructorReviewsManager';
import HomePageContentManager from './components/HomePageContentManager';

export default function AdminDashboardPage() {
  const { user } = useAuth();
  const { currentOrgId, currentOrg } = useOrg();
  
  const [activeTab, setActiveTab] = useState('overview'); // 'overview' | 'courses' | 'submissions' | 'students' | 'tools'
  const [copiedLink, setCopiedLink] = useState(false);
  const [copiedCourseId, setCopiedCourseId] = useState(null);
  const [loadingAnalytics, setLoadingAnalytics] = useState(true);
  const [analyticsData, setAnalyticsData] = useState(null);
  
  // Filters & search states
  const [courseSearch, setCourseSearch] = useState('');
  const [submissionFilter, setSubmissionFilter] = useState('all'); // 'all' | 'passed' | 'failed'
  const [submissionSearch, setSubmissionSearch] = useState('');
  const [studentSearch, setStudentSearch] = useState('');
  const [selectedSubmission, setSelectedSubmission] = useState(null);

  const role = (user?.role || '').toLowerCase();
  const isSuperAdmin = role === 'superadmin';
  const isAdmin = role === 'admin' || role === 'superadmin';
  const isInstructor = role === 'instructor';

  const originUrl = typeof window !== 'undefined' ? window.location.origin : 'https://learnhub.app';
  const instructorInviteUrl = `${originUrl}/signup?role=instructor&ref=${user?.referralCode || 'INST'}`;

  const copyInviteLink = () => {
    navigator.clipboard.writeText(instructorInviteUrl);
    setCopiedLink(true);
    setTimeout(() => setCopiedLink(false), 2500);
  };

  const copyCourseLink = (courseId) => {
    navigator.clipboard.writeText(`${originUrl}/courses/${courseId}`);
    setCopiedCourseId(courseId);
    setTimeout(() => setCopiedCourseId(null), 2500);
  };

  const fetchAnalytics = useCallback(async () => {
    try {
      setLoadingAnalytics(true);
      const res = await getInstructorAnalyticsApi(currentOrgId ? { organizationId: currentOrgId } : {});
      if (res && res.success) {
        setAnalyticsData(res);
      }
    } catch (err) {
      console.warn("Could not fetch remote instructor analytics, using store data:", err);
    } finally {
      setLoadingAnalytics(false);
    }
  }, [currentOrgId]);

  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  // Compute courses list for this instructor
  const displayCourses = useMemo(() => analyticsData?.courses || [], [analyticsData]);

  // Compute filtered courses
  const filteredCourses = useMemo(() => {
    if (!courseSearch.trim()) return displayCourses;
    const q = courseSearch.toLowerCase();
    return displayCourses.filter(c => 
      c.title?.toLowerCase().includes(q) || 
      c.category?.toLowerCase().includes(q) ||
      c.level?.toLowerCase().includes(q)
    );
  }, [displayCourses, courseSearch]);

  // Compute submissions list
  const submissionsList = useMemo(() => {
    const raw = analyticsData?.submissions || [];
    return raw.filter(sub => {
      if (submissionFilter === 'passed' && !sub.passed) return false;
      if (submissionFilter === 'failed' && sub.passed) return false;
      if (submissionSearch.trim()) {
        const q = submissionSearch.toLowerCase();
        return (
          sub.studentName?.toLowerCase().includes(q) ||
          sub.studentEmail?.toLowerCase().includes(q) ||
          sub.courseTitle?.toLowerCase().includes(q)
        );
      }
      return true;
    });
  }, [analyticsData, submissionFilter, submissionSearch]);

  // Compute enrolled students roster
  const enrolledStudents = useMemo(() => {
    const raw = analyticsData?.enrollments || [];
    if (!studentSearch.trim()) return raw;
    const q = studentSearch.toLowerCase();
    return raw.filter(e => 
      e.studentName?.toLowerCase().includes(q) ||
      e.studentEmail?.toLowerCase().includes(q) ||
      e.courseTitle?.toLowerCase().includes(q)
    );
  }, [analyticsData, studentSearch]);

  // Aggregate stats
  const stats = useMemo(() => {
    if (analyticsData?.stats) {
      return analyticsData.stats;
    }
    const totalLessons = displayCourses.reduce((sum, c) => {
      const secLectures = (c.sections || []).reduce((acc, s) => acc + (s.lectures ? s.lectures.length : 0), 0);
      return sum + (secLectures || c.lessons?.length || 0);
    }, 0);
    const totalStudents = displayCourses.reduce((sum, c) => sum + (c.students || 0), 0);
    const totalRevenue = displayCourses.reduce((sum, c) => sum + (c.price || 0) * (c.students || 0), 0);
    return {
      totalCourses: displayCourses.length,
      totalLessons,
      totalStudents,
      quizPassRate: 92,
      totalQuizzesTaken: 14,
      totalQuizzesPassed: 13,
      totalRevenue,
    };
  }, [analyticsData, displayCourses]);

  const hour = new Date().getHours();
  const greeting = hour < 12 ? 'Good morning' : hour < 17 ? 'Good afternoon' : 'Good evening';
  const displayName = user?.userName || (user?.firstName ? `${user.firstName} ${user.lastName || ''}` : isInstructor ? 'Instructor' : 'Administrator');

  const statCards = [
    {
      label: isInstructor ? 'My Courses' : 'Total Courses',
      value: stats.totalCourses,
      icon: 'ri-book-3-line',
      change: `${displayCourses.filter(c => c.isPublished !== false).length} Published`,
      positive: true,
      gradient: 'linear-gradient(135deg, #0d9488, #0f766e)',
      bg: 'rgba(20,184,166,0.08)',
    },
    {
      label: 'Active Students',
      value: stats.totalStudents.toLocaleString(),
      icon: 'ri-group-2-line',
      change: 'Enrolled across syllabus',
      positive: true,
      gradient: 'linear-gradient(135deg, #f59e0b, #d97706)',
      bg: 'rgba(245,158,11,0.08)',
    },
    {
      label: 'Checkpoint Pass Rate',
      value: `${stats.quizPassRate || 92}%`,
      icon: 'ri-checkbox-circle-line',
      change: `${stats.totalQuizzesPassed || 0} passed submissions`,
      positive: true,
      gradient: 'linear-gradient(135deg, #10b981, #059669)',
      bg: 'rgba(16,185,129,0.08)',
    },
    {
      label: isInstructor ? 'Est. Royalties' : 'Total Revenue',
      value: `$${(stats.totalRevenue || 0).toLocaleString()}`,
      icon: 'ri-money-dollar-circle-line',
      change: 'Platform enrollments',
      positive: true,
      gradient: 'linear-gradient(135deg, #6366f1, #4f46e5)',
      bg: 'rgba(99,102,241,0.08)',
    },
  ];

  return (
    <div className="space-y-6 max-w-full min-w-0">
      {/* Welcome banner */}
      <div
        className="rounded-3xl p-6 md:p-8 flex flex-col md:flex-row md:items-center justify-between gap-6 overflow-hidden relative shadow-lg"
        style={{
          background: 'linear-gradient(120deg, #0f172a 0%, #1e3a5f 60%, #0d9488 100%)',
        }}
      >
        <div
          className="absolute inset-0 opacity-10"
          style={{
            backgroundImage:
              'radial-gradient(circle at 80% 50%, #2dd4bf 0%, transparent 60%)',
          }}
        />
        <div className="relative">
          <div className="flex flex-wrap items-center gap-2 mb-2">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 backdrop-blur-md text-teal-300 text-[11px] font-bold border border-white/10">
              <span className="w-2 h-2 rounded-full bg-teal-400 animate-pulse" />
              {isSuperAdmin ? '👑 Super Administrator Workspace' : isAdmin ? '⚡ Administrator Center' : '🎓 Instructor Teaching Portal'}
            </div>
            {currentOrg && (
              <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-purple-500/20 backdrop-blur-md text-purple-200 text-[11px] font-bold border border-purple-400/30">
                <i className="ri-building-2-line" />
                {currentOrg.name}
              </div>
            )}
          </div>
          <h2 className="text-2xl md:text-3xl font-extrabold text-white mb-1.5">
            {greeting}, {displayName} 👋
          </h2>
          <p className="text-slate-300 text-xs md:text-sm max-w-xl">
            {isInstructor 
              ? 'Manage your course curriculum, monitor student quiz checkpoints, track enrolled learners, and publish lectures.'
              : isSuperAdmin
              ? 'Full control over platform admins, instructors, curricula, and system configuration.'
              : 'Oversee all platform educational content, faculty members, and student progression.'}
          </p>
        </div>

        <div className="relative flex flex-wrap items-center gap-3">
          <button
            type="button"
            onClick={fetchAnalytics}
            disabled={loadingAnalytics}
            className="flex items-center gap-1.5 px-3.5 py-2.5 rounded-xl text-xs font-semibold text-white bg-white/10 hover:bg-white/20 border border-white/15 transition-all cursor-pointer"
            title="Refresh dashboard stats"
          >
            <i className={`ri-refresh-line text-sm ${loadingAnalytics ? 'animate-spin' : ''}`} />
            Refresh
          </button>
          {isAdmin && (
            <Link
              to="/admin/content"
              className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-xs font-bold text-white whitespace-nowrap cursor-pointer transition-all bg-indigo-600 hover:bg-indigo-500 border border-indigo-400/40 shadow-sm hover:scale-[1.02]"
              title="Edit homepage and static website content"
            >
              <i className="ri-layout-masonry-line text-sm" />
              Manage Static Content (CMS)
            </Link>
          )}
          {isAdmin && (
            <Link
              to="/admin/instructors"
              className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-xs font-bold text-white whitespace-nowrap cursor-pointer transition-all bg-white/15 hover:bg-white/25 border border-white/20 shadow-xs"
            >
              <i className="ri-user-add-line text-sm" />
              Add Instructor
            </Link>
          )}
          <Link
            to="/admin/courses/new"
            className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-xs font-bold text-slate-950 whitespace-nowrap cursor-pointer transition-all bg-teal-400 hover:bg-teal-300 shadow-md hover:scale-[1.02]"
          >
            <i className="ri-add-circle-line text-sm" />
            Create Course
          </Link>
        </div>
      </div>

      {/* Instructor Invitation Quick Bar */}
      {isAdmin && (
        <div className="bg-white rounded-2xl p-4 border border-slate-200/80 shadow-xs flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-teal-50 text-teal-600 flex items-center justify-center font-bold text-base flex-shrink-0">
              <i className="ri-share-forward-line" />
            </div>
            <div>
              <h3 className="text-xs font-bold text-slate-900">Invite New Instructors to Sign Up</h3>
              <p className="text-[11px] text-slate-500">
                Share this dedicated signup link so faculty can self-register with verified Instructor privileges.
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <input
              type="text"
              readOnly
              value={instructorInviteUrl}
              className="px-3 py-1.5 bg-slate-50 border border-slate-200 rounded-lg text-xs font-mono text-slate-600 max-w-xs truncate select-all"
            />
            <button
              type="button"
              onClick={copyInviteLink}
              className="px-3 py-1.5 bg-slate-900 hover:bg-slate-800 text-white font-bold text-xs rounded-lg transition-all cursor-pointer whitespace-nowrap"
            >
              {copiedLink ? 'Copied!' : 'Copy Link'}
            </button>
            <Link
              to="/admin/instructors"
              className="px-3 py-1.5 bg-teal-50 hover:bg-teal-100 text-teal-700 font-bold text-xs rounded-lg transition-all"
            >
              Manage Instructors &rarr;
            </Link>
          </div>
        </div>
      )}

      {/* Stat cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {statCards.map((card) => (
          <div
            key={card.label}
            className="bg-white rounded-2xl p-5 flex flex-col gap-3 border border-slate-100 shadow-xs"
          >
            <div className="flex items-start justify-between">
              <div
                className="w-10 h-10 flex items-center justify-center rounded-xl text-white text-base"
                style={{ background: card.gradient }}
              >
                <i className={card.icon} />
              </div>
              <span
                className="text-[10px] font-bold px-2 py-0.5 rounded-full"
                style={{
                  background: card.bg,
                  color: card.gradient.includes('0d9488')
                    ? '#0d9488'
                    : card.gradient.includes('f59e0b')
                    ? '#d97706'
                    : card.gradient.includes('10b981')
                    ? '#059669'
                    : '#4f46e5',
                }}
              >
                Active
              </span>
            </div>
            <div>
              <p className="text-2xl font-extrabold text-slate-900 leading-none">{card.value}</p>
              <p className="text-xs text-slate-500 mt-1">{card.label}</p>
            </div>
            <p className="text-[11px] text-slate-400 font-medium">{card.change}</p>
          </div>
        ))}
      </div>

      {/* Main Interactive Navigation Tabs */}
      <div className="flex items-center gap-2 border-b border-slate-200 pb-1 overflow-x-auto max-w-full">
        {[
          { id: 'overview', label: '📊 Overview & Analytics', badge: null },
          ...(isAdmin ? [{ id: 'cms', label: '🎨 Static Content (CMS)', badge: 'Live' }] : []),
          { id: 'courses', label: '📚 My Courses & Curriculum', badge: displayCourses.length },
          { id: 'reviews', label: '⭐ Student Reviews & Feedback', badge: null },
          { id: 'submissions', label: '📝 Quiz Checkpoints', badge: submissionsList.length },
          { id: 'students', label: '👥 Enrolled Students', badge: enrolledStudents.length },
          { id: 'tools', label: '⚡ Curriculum Tools & Actions', badge: null },
        ].map((tab) => (
          <button
            key={tab.id}
            type="button"
            onClick={() => setActiveTab(tab.id)}
            className={`px-4 py-2.5 rounded-xl font-bold text-xs whitespace-nowrap transition-all flex items-center gap-2 cursor-pointer ${
              activeTab === tab.id
                ? 'bg-slate-900 text-white shadow-sm'
                : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100'
            }`}
          >
            <span>{tab.label}</span>
            {tab.badge !== null && (
              <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${
                activeTab === tab.id ? 'bg-teal-400 text-slate-950' : 'bg-slate-200 text-slate-700'
              }`}>
                {tab.badge}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* TAB 1: OVERVIEW & ANALYTICS */}
      {activeTab === 'overview' && (
        <div className="space-y-6 animate-fade-in">
          {/* Static Content Management Quick Callout */}
          {isAdmin && (
            <div className="bg-gradient-to-r from-indigo-50/90 via-purple-50/70 to-teal-50/80 border border-indigo-100 rounded-3xl p-5 md:p-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 shadow-2xs">
              <div className="flex items-center gap-3.5">
                <div className="w-12 h-12 rounded-2xl bg-indigo-600 text-white flex items-center justify-center text-xl shadow-md flex-shrink-0">
                  <i className="ri-layout-masonry-line" />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <h4 className="font-bold text-slate-900 text-sm md:text-base">Website Static Content Manager (CMS)</h4>
                    <span className="text-[10px] font-extrabold px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-800">
                      Live
                    </span>
                  </div>
                  <p className="text-xs text-slate-600 mt-0.5 max-w-2xl">
                    Manage hero banners, trust metrics, why learnflow value propositions, student testimonials, and CTA banners without code.
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0 w-full sm:w-auto">
                <button
                  type="button"
                  onClick={() => setActiveTab('cms')}
                  className="w-full sm:w-auto px-4 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white font-bold text-xs rounded-xl transition-all shadow-xs cursor-pointer flex items-center justify-center gap-2"
                >
                  <i className="ri-edit-2-line text-sm" />
                  Open CMS Editor &rarr;
                </button>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Top Performing Courses */}
            <div className="bg-white rounded-3xl overflow-hidden border border-slate-100 shadow-xs">
              <div className="px-6 py-4 flex items-center justify-between border-b border-slate-100">
                <div>
                  <h3 className="font-bold text-slate-900 text-sm">Active Curriculum Overview</h3>
                  <p className="text-[11px] text-slate-400 mt-0.5">Courses by enrolled learners</p>
                </div>
                <button
                  type="button"
                  onClick={() => setActiveTab('courses')}
                  className="text-xs font-semibold text-teal-600 hover:text-teal-700 cursor-pointer"
                >
                  Manage All &rarr;
                </button>
              </div>
              <div className="p-4 space-y-3">
                {displayCourses.slice(0, 5).map((course, index) => {
                  const max = Math.max(...displayCourses.map(c => c.students || 1), 1);
                  const pct = Math.round(((course.students || 0) / max) * 100);
                  return (
                    <div key={course._id || course.id || index} className="flex items-center gap-3 p-2 hover:bg-slate-50 rounded-xl transition-colors">
                      <span
                        className="w-6 h-6 flex items-center justify-center rounded-full text-xs font-extrabold flex-shrink-0"
                        style={{
                          background:
                            index === 0
                              ? 'linear-gradient(135deg,#f59e0b,#d97706)'
                              : index === 1
                              ? 'linear-gradient(135deg,#94a3b8,#64748b)'
                              : 'linear-gradient(135deg,#0d9488,#0f766e)',
                          color: '#fff',
                        }}
                      >
                        {index + 1}
                      </span>
                      <img
                        src={course.thumbnail || 'https://images.unsplash.com/photo-1516321318423-f06f85e504b3?w=120&auto=format&fit=crop'}
                        alt={course.title}
                        className="w-10 h-8 rounded-lg object-cover object-top flex-shrink-0"
                      />
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-bold text-slate-800 truncate">{course.title}</p>
                        <div className="flex items-center gap-2 mt-1">
                          <div className="flex-1 h-1.5 rounded-full overflow-hidden bg-slate-100">
                            <div
                              className="h-full rounded-full"
                              style={{
                                width: `${pct}%`,
                                background: 'linear-gradient(90deg,#0d9488,#2dd4bf)',
                              }}
                            />
                          </div>
                          <span className="text-[11px] text-slate-500 flex-shrink-0 font-medium">
                            {(course.students || 0).toLocaleString()} students
                          </span>
                        </div>
                      </div>
                      <Link
                        to={`/admin/courses/${course._id || course.id}/edit`}
                        className="w-7 h-7 flex items-center justify-center rounded-lg text-slate-400 hover:text-teal-600 hover:bg-teal-50 transition-colors"
                      >
                        <i className="ri-edit-line text-xs" />
                      </Link>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Recent Checkpoint Activity Stream */}
            <div className="bg-white rounded-3xl overflow-hidden border border-slate-100 shadow-xs">
              <div className="px-6 py-4 flex items-center justify-between border-b border-slate-100">
                <div>
                  <h3 className="font-bold text-slate-900 text-sm">Recent Student Assessments</h3>
                  <p className="text-[11px] text-slate-400 mt-0.5">Real-time lesson checkpoints submitted</p>
                </div>
                <button
                  type="button"
                  onClick={() => setActiveTab('submissions')}
                  className="text-xs font-semibold text-teal-600 hover:text-teal-700 cursor-pointer"
                >
                  View All Submissions &rarr;
                </button>
              </div>
              <div className="divide-y divide-slate-100">
                {submissionsList.length > 0 ? (
                  submissionsList.slice(0, 5).map((sub) => (
                    <div
                      key={sub.id}
                      className="flex items-center justify-between gap-3 px-6 py-3.5 hover:bg-slate-50/70 transition-colors"
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <div className={`w-8 h-8 rounded-xl flex items-center justify-center text-xs font-bold text-white flex-shrink-0 ${
                          sub.passed ? 'bg-emerald-500' : 'bg-rose-500'
                        }`}>
                          {sub.passed ? '✓' : '✕'}
                        </div>
                        <div className="min-w-0">
                          <p className="text-xs font-bold text-slate-800 truncate">{sub.studentName}</p>
                          <p className="text-[11px] text-slate-400 truncate">{sub.courseTitle}</p>
                        </div>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <span className={`text-xs font-extrabold ${sub.passed ? 'text-emerald-600' : 'text-rose-600'}`}>
                          {sub.score}%
                        </span>
                        <p className="text-[10px] text-slate-400">
                          {sub.submittedAt ? new Date(sub.submittedAt).toLocaleDateString() : 'Recent'}
                        </p>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="p-8 text-center text-slate-400 text-xs">
                    <i className="ri-inbox-line text-2xl mb-1 block" />
                    No quiz checkpoint submissions recorded yet.
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* TAB 2: MY COURSES & CURRICULUM */}
      {activeTab === 'courses' && (
        <div className="space-y-6 animate-fade-in">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-white p-4 rounded-2xl border border-slate-200 shadow-xs">
            <div className="relative flex-1 max-w-md">
              <i className="ri-search-line absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 text-xs" />
              <input
                type="text"
                placeholder="Search courses by title, category, or level..."
                value={courseSearch}
                onChange={(e) => setCourseSearch(e.target.value)}
                className="w-full pl-9 pr-4 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs font-medium text-slate-800 focus:outline-none focus:border-teal-500"
              />
            </div>
            <Link
              to="/admin/courses/new"
              className="px-4 py-2 bg-teal-600 hover:bg-teal-700 text-white font-bold text-xs rounded-xl transition-all inline-flex items-center gap-1.5 shadow-sm"
            >
              <i className="ri-add-line text-sm" /> Create New Course
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredCourses.map((course) => {
              const cId = course._id || course.id;
              const lecturesCount = (course.sections || []).reduce((acc, s) => acc + (s.lectures ? s.lectures.length : 0), 0) || (course.lessons?.length || 0);

              return (
                <div
                  key={cId}
                  className="bg-white rounded-3xl border border-slate-200/80 overflow-hidden shadow-xs hover:shadow-md transition-all flex flex-col justify-between"
                >
                  <div>
                    <div className="relative h-44 bg-slate-100 overflow-hidden group">
                      <img
                        src={course.thumbnail || 'https://images.unsplash.com/photo-1516321318423-f06f85e504b3?w=600&auto=format&fit=crop'}
                        alt={course.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                      <div className="absolute top-3 left-3 flex gap-2">
                        <span className="px-2.5 py-1 rounded-full bg-slate-900/80 backdrop-blur-md text-white font-bold text-[10px]">
                          {course.category || 'General'}
                        </span>
                        <span className="px-2.5 py-1 rounded-full bg-teal-600/90 backdrop-blur-md text-white font-bold text-[10px]">
                          {course.level || 'All Levels'}
                        </span>
                      </div>
                      <div className="absolute top-3 right-3">
                        <span className={`px-2.5 py-1 rounded-full text-[10px] font-bold shadow-xs ${
                          course.isPublished !== false ? 'bg-emerald-500 text-white' : 'bg-amber-500 text-white'
                        }`}>
                          {course.isPublished !== false ? 'Published' : 'Draft'}
                        </span>
                      </div>
                    </div>

                    <div className="p-5">
                      <h4 className="font-bold text-slate-900 text-sm mb-1.5 line-clamp-2">{course.title}</h4>
                      <p className="text-slate-500 text-xs line-clamp-2 mb-4 leading-relaxed">{course.description}</p>

                      <div className="flex items-center justify-between text-xs text-slate-500 border-t border-slate-100 pt-3">
                        <span className="flex items-center gap-1">
                          <i className="ri-play-circle-line text-teal-600" />
                          <strong>{lecturesCount}</strong> lectures
                        </span>
                        <span className="flex items-center gap-1">
                          <i className="ri-group-line text-amber-500" />
                          <strong>{(course.students || 0).toLocaleString()}</strong> students
                        </span>
                        <span className="font-bold text-slate-900">
                          {course.isPaid ? `$${course.price}` : 'Free'}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="p-4 bg-slate-50 border-t border-slate-100 flex items-center justify-between gap-2">
                    <Link
                      to={`/admin/courses/${cId}/edit`}
                      className="flex-1 py-2 px-3 bg-teal-600 hover:bg-teal-700 text-white font-bold text-xs rounded-xl text-center transition-all shadow-xs"
                    >
                      <i className="ri-edit-line mr-1" /> Edit Syllabus
                    </Link>
                    <Link
                      to={`/courses/${cId}`}
                      className="py-2 px-3 bg-white hover:bg-slate-100 text-slate-700 font-semibold text-xs rounded-xl border border-slate-200 transition-all text-center"
                      title="Preview course as student"
                    >
                      <i className="ri-eye-line" />
                    </Link>
                    <button
                      type="button"
                      onClick={() => copyCourseLink(cId)}
                      className="py-2 px-3 bg-white hover:bg-slate-100 text-slate-700 font-semibold text-xs rounded-xl border border-slate-200 transition-all cursor-pointer"
                      title="Copy student enrollment link"
                    >
                      <i className={copiedCourseId === cId ? "ri-check-line text-teal-600 font-bold" : "ri-share-line"} />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* TAB 3: STUDENT CHECKPOINT SUBMISSIONS & GRADING */}
      {activeTab === 'submissions' && (
        <div className="space-y-6 animate-fade-in">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-white p-4 rounded-2xl border border-slate-200 shadow-xs">
            <div className="relative flex-1 max-w-md">
              <i className="ri-search-line absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 text-xs" />
              <input
                type="text"
                placeholder="Search by student name, email, or course..."
                value={submissionSearch}
                onChange={(e) => setSubmissionSearch(e.target.value)}
                className="w-full pl-9 pr-4 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs font-medium text-slate-800 focus:outline-none focus:border-teal-500"
              />
            </div>
            
            <div className="flex items-center gap-2">
              <span className="text-xs text-slate-500 font-semibold">Filter:</span>
              <button
                type="button"
                onClick={() => setSubmissionFilter('all')}
                className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${
                  submissionFilter === 'all' ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
              >
                All Submissions
              </button>
              <button
                type="button"
                onClick={() => setSubmissionFilter('passed')}
                className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${
                  submissionFilter === 'passed' ? 'bg-emerald-600 text-white' : 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100'
                }`}
              >
                Passed (70%+)
              </button>
              <button
                type="button"
                onClick={() => setSubmissionFilter('failed')}
                className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${
                  submissionFilter === 'failed' ? 'bg-rose-600 text-white' : 'bg-rose-50 text-rose-700 hover:bg-rose-100'
                }`}
              >
                Needs Retake
              </button>
            </div>
          </div>

          <div className="bg-white rounded-3xl border border-slate-200 overflow-hidden shadow-xs">
            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs">
                <thead className="bg-slate-50 border-b border-slate-200 text-slate-500 font-bold uppercase text-[10px] tracking-wider">
                  <tr>
                    <th className="px-6 py-4">Student</th>
                    <th className="px-6 py-4">Course</th>
                    <th className="px-6 py-4">Assessment Type</th>
                    <th className="px-6 py-4">Score & Result</th>
                    <th className="px-6 py-4">Submitted Date</th>
                    <th className="px-6 py-4 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {submissionsList.length > 0 ? (
                    submissionsList.map((sub) => (
                      <tr key={sub.id} className="hover:bg-slate-50/70 transition-colors">
                        <td className="px-6 py-4 font-semibold text-slate-900">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full bg-teal-50 text-teal-700 font-bold flex items-center justify-center text-xs">
                              {sub.studentName?.charAt(0).toUpperCase() || 'S'}
                            </div>
                            <div>
                              <p className="font-bold text-slate-900">{sub.studentName}</p>
                              <p className="text-[11px] text-slate-400 font-normal">{sub.studentEmail}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 text-slate-700 font-medium">
                          <span className="line-clamp-1">{sub.courseTitle}</span>
                        </td>
                        <td className="px-6 py-4 text-slate-600">
                          <span className="px-2.5 py-1 rounded-lg bg-slate-100 text-slate-700 font-semibold text-[11px]">
                            {sub.type === 'final' ? '🏆 Final Course Quiz' : '📝 Lesson Checkpoint'}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-2">
                            <span className={`px-2.5 py-1 rounded-full font-extrabold text-[11px] ${
                              sub.passed ? 'bg-emerald-100 text-emerald-800' : 'bg-rose-100 text-rose-800'
                            }`}>
                              {sub.score}% — {sub.passed ? 'PASSED' : 'RETAKE REQUIRED'}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4 text-slate-500 font-mono text-[11px]">
                          {sub.submittedAt ? new Date(sub.submittedAt).toLocaleDateString() : 'Recent'}
                        </td>
                        <td className="px-6 py-4 text-right">
                          <button
                            type="button"
                            onClick={() => setSelectedSubmission(sub)}
                            className="px-3 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold rounded-lg text-xs transition-colors cursor-pointer"
                          >
                            Review Details
                          </button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="6" className="px-6 py-12 text-center text-slate-400">
                        <i className="ri-folder-open-line text-3xl block mb-2" />
                        No submissions match the current filters.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* TAB 4: ENROLLED STUDENTS DIRECTORY */}
      {activeTab === 'students' && (
        <div className="space-y-6 animate-fade-in">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-white p-4 rounded-2xl border border-slate-200 shadow-xs">
            <div className="relative flex-1 max-w-md">
              <i className="ri-search-line absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 text-xs" />
              <input
                type="text"
                placeholder="Search students by name, email, or course..."
                value={studentSearch}
                onChange={(e) => setStudentSearch(e.target.value)}
                className="w-full pl-9 pr-4 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs font-medium text-slate-800 focus:outline-none focus:border-teal-500"
              />
            </div>
            <div className="text-xs text-slate-500 font-semibold">
              Total Enrolled Learners: <strong className="text-slate-900">{enrolledStudents.length}</strong>
            </div>
          </div>

          <div className="bg-white rounded-3xl border border-slate-200 overflow-hidden shadow-xs">
            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs">
                <thead className="bg-slate-50 border-b border-slate-200 text-slate-500 font-bold uppercase text-[10px] tracking-wider">
                  <tr>
                    <th className="px-6 py-4">Student</th>
                    <th className="px-6 py-4">Enrolled Course</th>
                    <th className="px-6 py-4">Completed Lessons</th>
                    <th className="px-6 py-4">Course Status</th>
                    <th className="px-6 py-4">Enrolled Date</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {enrolledStudents.length > 0 ? (
                    enrolledStudents.map((enroll) => (
                      <tr key={enroll._id} className="hover:bg-slate-50/70 transition-colors">
                        <td className="px-6 py-4 font-semibold text-slate-900">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full bg-slate-800 text-teal-300 font-bold flex items-center justify-center text-xs">
                              {enroll.studentName?.charAt(0).toUpperCase() || 'S'}
                            </div>
                            <div>
                              <p className="font-bold text-slate-900">{enroll.studentName}</p>
                              <p className="text-[11px] text-slate-400 font-normal">{enroll.studentEmail}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 text-slate-700 font-medium">
                          {enroll.courseTitle}
                        </td>
                        <td className="px-6 py-4 text-slate-700">
                          <span className="font-bold text-slate-900">{enroll.completedLessonsCount || 0}</span> lessons completed
                        </td>
                        <td className="px-6 py-4">
                          <span className={`px-2.5 py-1 rounded-full text-[10px] font-bold ${
                            enroll.courseCompleted
                              ? 'bg-emerald-100 text-emerald-800'
                              : 'bg-amber-100 text-amber-800'
                          }`}>
                            {enroll.courseCompleted ? '✓ Course Completed' : 'In Progress'}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-slate-500 font-mono text-[11px]">
                          {enroll.enrolledAt ? new Date(enroll.enrolledAt).toLocaleDateString() : 'Active'}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="5" className="px-6 py-12 text-center text-slate-400">
                        <i className="ri-user-unfollow-line text-3xl block mb-2" />
                        No students found matching the search criteria.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* TAB 5: CURRICULUM TOOLS & ACTIONS */}
      {activeTab === 'tools' && (
        <div className="space-y-6 animate-fade-in">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-white rounded-3xl p-6 border border-slate-200 shadow-xs flex flex-col justify-between">
              <div>
                <div className="w-12 h-12 rounded-2xl bg-teal-50 text-teal-600 flex items-center justify-center text-2xl font-bold mb-4">
                  <i className="ri-folder-add-line" />
                </div>
                <h4 className="font-bold text-slate-900 text-sm mb-1">Create New Curriculum</h4>
                <p className="text-slate-500 text-xs leading-relaxed mb-6">
                  Build structured chapters with video streams, dynamic slide decks, practical task uploads, and graded checkpoints.
                </p>
              </div>
              <Link
                to="/admin/courses/new"
                className="w-full py-2.5 bg-teal-600 hover:bg-teal-700 text-white font-bold text-xs rounded-xl text-center transition-all shadow-sm"
              >
                Launch Course Builder &rarr;
              </Link>
            </div>

            <div className="bg-white rounded-3xl p-6 border border-slate-200 shadow-xs flex flex-col justify-between">
              <div>
                <div className="w-12 h-12 rounded-2xl bg-amber-50 text-amber-600 flex items-center justify-center text-2xl font-bold mb-4">
                  <i className="ri-questionnaire-line" />
                </div>
                <h4 className="font-bold text-slate-900 text-sm mb-1">Assessment Engine</h4>
                <p className="text-slate-500 text-xs leading-relaxed mb-6">
                  Add multiple-choice quizzes, code validation exercises, and task screenshots to any lecture in your syllabus.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setActiveTab('courses')}
                className="w-full py-2.5 bg-slate-900 hover:bg-slate-800 text-white font-bold text-xs rounded-xl text-center transition-all shadow-sm cursor-pointer"
              >
                Select Course to Edit &rarr;
              </button>
            </div>

            <div className="bg-white rounded-3xl p-6 border border-slate-200 shadow-xs flex flex-col justify-between">
              <div>
                <div className="w-12 h-12 rounded-2xl bg-purple-50 text-purple-600 flex items-center justify-center text-2xl font-bold mb-4">
                  <i className="ri-user-shared-line" />
                </div>
                <h4 className="font-bold text-slate-900 text-sm mb-1">Educator Referral Link</h4>
                <p className="text-slate-500 text-xs leading-relaxed mb-6">
                  Invite students directly to your faculty profile or faculty members to register with instructor privileges.
                </p>
              </div>
              <button
                type="button"
                onClick={copyInviteLink}
                className="w-full py-2.5 bg-purple-600 hover:bg-purple-700 text-white font-bold text-xs rounded-xl text-center transition-all shadow-sm cursor-pointer"
              >
                {copiedLink ? '✓ Copied Link' : 'Copy Invitation Link'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* TAB: STUDENT REVIEWS & RATINGS */}
      {activeTab === 'reviews' && (
        <div className="animate-fade-in">
          <InstructorReviewsManager />
        </div>
      )}

      {/* TAB: HOME PAGE CMS MANAGER */}
      {activeTab === 'cms' && (
        <div className="animate-fade-in">
          <HomePageContentManager />
        </div>
      )}

      {/* Modal: Review Submission Details */}
      {selectedSubmission && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-xs p-4 animate-fade-in">
          <div className="bg-white rounded-3xl max-w-lg w-full p-6 md:p-8 shadow-2xl border border-slate-100">
            <div className="flex items-start justify-between mb-4">
              <div>
                <span className={`px-2.5 py-1 rounded-full text-[10px] font-bold uppercase ${
                  selectedSubmission.passed ? 'bg-emerald-100 text-emerald-800' : 'bg-rose-100 text-rose-800'
                }`}>
                  {selectedSubmission.passed ? 'Passed Assessment' : 'Needs Retake'}
                </span>
                <h3 className="text-lg font-bold text-slate-900 mt-2">Assessment Submission</h3>
                <p className="text-xs text-slate-500">{selectedSubmission.courseTitle}</p>
              </div>
              <button
                type="button"
                onClick={() => setSelectedSubmission(null)}
                className="w-8 h-8 rounded-full bg-slate-100 text-slate-500 hover:bg-slate-200 flex items-center justify-center cursor-pointer"
              >
                <i className="ri-close-line text-lg" />
              </button>
            </div>

            <div className="space-y-3 py-4 border-y border-slate-100 text-xs">
              <div className="flex justify-between">
                <span className="text-slate-500">Student Name:</span>
                <strong className="text-slate-900">{selectedSubmission.studentName}</strong>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">Student Email:</span>
                <strong className="text-slate-900">{selectedSubmission.studentEmail}</strong>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">Score Achieved:</span>
                <strong className={`font-extrabold ${selectedSubmission.passed ? 'text-emerald-600' : 'text-rose-600'}`}>
                  {selectedSubmission.score}%
                </strong>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">Submission Type:</span>
                <strong className="text-slate-900">{selectedSubmission.type === 'final' ? 'Final Exam' : 'Lesson Quiz Checkpoint'}</strong>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">Date:</span>
                <span className="text-slate-700">{selectedSubmission.submittedAt ? new Date(selectedSubmission.submittedAt).toLocaleString() : 'N/A'}</span>
              </div>
            </div>

            <div className="mt-6 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setSelectedSubmission(null)}
                className="px-5 py-2.5 bg-slate-900 text-white font-bold text-xs rounded-xl hover:bg-slate-800 transition-all cursor-pointer"
              >
                Close Review
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

